import * as React from 'react';

import { Autocomplete, Box, Button, Checkbox, FormControl, FormControlLabel, InputLabel, OutlinedInput, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { DataGrid, GridColDef, GridRowParams, GridRenderCellParams } from '@mui/x-data-grid';
import { Interface } from '../../types/Interface';
import { copyArrayWithIndexChanged } from '../../Utils';
import { AlertProps } from '@mui/material/Alert';
import { useSnackbar } from '../../../contexts/SnackBarContext';
import { TestCombination, TestComboSupportVal, getTestComboSupport, updateTestComboSupport, deleteTestComboSupport } from '../../types/TestCombinations';
import { TestCase, TestcaseStatusStrings, TestcaseFieldTypeStrings, getTestCases } from '../../types/TestCase';

import { httpPost } from "../../Web";
import { ApiResponse } from "../../types/ApiResponse";

const TestcaseColumns: GridColDef[] = [{
        field: 'isValid',
        headerName: 'Valid',
        flex: 1,
        renderCell: (params: GridRenderCellParams<any, boolean>) => {
            if (params.row.isValid === false) {
                return <CloseIcon color={'error'} />
            }
            if (params.row.isValid === true) {
                return <CheckIcon color={'success'} />
            }
            return <RemoveCircleOutlineIcon />
        },
    },
    {
        field: 'group',
        headerName: 'Group',
        flex: 1
    },
    {
        field: 'name',
        headerName: 'Name',
        flex: 3
    },
    {
        field: 'description',
        headerName: 'Description',
        flex: 3
    },
    {
        field: 'sortOrder',
        headerName: 'Sort Order',
        flex: 1
    },
    {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        valueFormatter: (value?: number) => {
            if (value == null) {
                return '';
            }
            return TestcaseStatusStrings[value];
        }
    },
    {
        field: 'fieldType',
        headerName: 'Field Type',
        flex: 1,
        valueFormatter: (value?: number) => {
            if (value == null) {
                return '';
            }
            return TestcaseFieldTypeStrings[value];
        }
    },
    {
        field: 'fieldSchema',
        headerName: 'Field Schema',
        flex: 2
    },
    {
        field: 'id',
        headerName: 'ID',
        flex: 1
    }
];

export const GetEmptyTestCase = (): TestCase => ({
    id: 0,
    guid: '',
    fieldSchema: '',
    fieldType: 0,
    status: 0,
    sortOrder: 0,
    name: '',
    description: '',
    group: '',
    interfaceId: 0
});

interface RowValue extends TestCase {
    isValid?: boolean; // This is used to indicate if the TestCase is valid for the selected test combination
};
const getRowValues = (testCases: TestCase[], allSupportedCombo: TestComboSupportVal[]): RowValue[] => {
    if (testCases.length === 0) {
        return [];
    }
    let rowvalues: RowValue[] = [];
    testCases.forEach(tc => {
        const isValid = allSupportedCombo.some(combo => combo.testCaseId === tc.id && combo.testCombinationId !== undefined);
        const value: RowValue = { ...tc, isValid: isValid };
        rowvalues.push(value);
    });
    return rowvalues;
}

const updateTestcase = (data: TestCase, abortController: AbortController): Promise<ApiResponse<TestCase>> => httpPost(`/api/interface/testcase/update`, data, abortController);

export const TestcaseAdmin = ({ selectedTestCombination, selectedInterface, selectedTestCase, setSelectedTestCase }:
    { selectedTestCombination: TestCombination, selectedInterface: Interface, selectedTestCase: TestCase, setSelectedTestCase: (value: React.SetStateAction<TestCase>) => void }) => {
    const { showSnackbar } = useSnackbar();
    const [allTestcases, setAllTestcases] = React.useState<TestCase[]>([]);
    const [rowvalues, setRowvalues] = React.useState<RowValue[]>([]);
    const [allSupportedCombo, setAllSupportedCombo] = React.useState<TestComboSupportVal[]>([]);
    const [selectedCombo, setselectedCombo] = React.useState<TestComboSupportVal>({});
    const [alteredCombo, setAlteredCombo] = React.useState<TestComboSupportVal>({});

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    
    React.useEffect(() => {
        const fetchTestCombinations = async () => {
            if(!selectedInterface || selectedInterface.id === undefined) {
                setAllTestcases([]);
                setRowvalues([]);
                return;
            };
            const allTestcasesResponse = await getTestCases(selectedInterface.id, new AbortController());
            if (allTestcasesResponse.failed) {
                showUserFeedback(allTestcasesResponse.errorText);
                return;
            }
            setAllTestcases(allTestcasesResponse.result);
            setRowvalues(getRowValues(allTestcasesResponse.result, []));
        }
        fetchTestCombinations();
    }, [showUserFeedback, selectedInterface]);
    
    React.useEffect(() => {
        const fetchSupportedCombinations = async () => {
            if (!selectedTestCombination || selectedTestCombination.id === undefined
                || selectedTestCombination.id === 0 || selectedInterface.id === 0
                || !selectedInterface || selectedInterface.id === undefined) {
                setAllSupportedCombo([]);
                setRowvalues(getRowValues(allTestcases, []));
                return;
            }

            const testComboSupport: TestComboSupportVal = {
                testCombinationId: selectedTestCombination.id,
                interfaceId: selectedInterface.id,
                testCaseId: undefined, // all test cases will be fetched
                interfacePropertyId: 0 // 0 means not applicable
            }
            const allcombosResponse = await getTestComboSupport(testComboSupport, new AbortController());
            if (allcombosResponse.failed) {
                showUserFeedback(allcombosResponse.errorText);
                return;
            }
            setAllSupportedCombo(allcombosResponse.result);
            setRowvalues(getRowValues(allTestcases, allcombosResponse.result));
            setSelectedTestCase(GetEmptyTestCase());
            setselectedCombo({});
            setAlteredCombo({});
        }
        fetchSupportedCombinations();
    }, [showUserFeedback, setSelectedTestCase, selectedTestCombination, allTestcases, selectedInterface]);

    const saveHandler = async (testcaseData: TestCase) => {
        let isNewEntry = false;

        if (testcaseData.interfaceId === undefined || testcaseData.interfaceId === 0) {
            testcaseData.interfaceId = selectedInterface.id;
            isNewEntry = true;
        }

        const updatedTestcase = await updateTestcase(testcaseData, new AbortController());
        if (updatedTestcase.failed) {
            showUserFeedback(updatedTestcase.errorText);
            return;
        }
        const updatedTestcaseLoc = updatedTestcase.result;
        setSelectedTestCase(GetEmptyTestCase());
        let allTestcasesLoc = copyArrayWithIndexChanged(allTestcases, allTestcases.findIndex(tc => tc.id === updatedTestcaseLoc.id), updatedTestcaseLoc);
        setAllTestcases(allTestcasesLoc);

        if (selectedTestCombination.id !== undefined && selectedTestCombination.id > 0) {
            if (selectedCombo.testCaseId !== alteredCombo.testCaseId) {
                if (isNewEntry) {
                    alteredCombo.testCaseId = updatedTestcaseLoc.id;
                }
                if (alteredCombo.testCaseId) {
                    updateTestComboSupport(alteredCombo, new AbortController()).then((response) => {
                        if (response.failed) {
                            showUserFeedback(response.errorText);
                            return;
                        }
                        const updatedCombo = response.result;
                        const allSupportedComboLoc = copyArrayWithIndexChanged(allSupportedCombo, allSupportedCombo.findIndex(combo => combo.id === updatedCombo.id), updatedCombo);
                        setAllSupportedCombo(allSupportedComboLoc);
                    });
                }
                else if (selectedCombo.id !== undefined) {
                    deleteTestComboSupport(selectedCombo.id ?? 0, new AbortController()).then((response) => {
                        if (response.failed) {
                            showUserFeedback(response.errorText);
                            return;
                        }
                        const updatedCombo = response.result;
                        const allSupportedComboLoc = allSupportedCombo.filter(combo => combo.id !== updatedCombo.id);
                        setAllSupportedCombo(allSupportedComboLoc);
                    });
                }
                setselectedCombo({});
                setAlteredCombo({});
            }
        }
    }
    
    const onSubmitHandler = (e: { preventDefault: () => void; }) => {
        e.preventDefault();
        saveHandler(selectedTestCase);
    }
    
    const onSelectionChange = (sel: GridRowParams) => {
        if (sel.id === selectedTestCase.id) {
            setSelectedTestCase(GetEmptyTestCase()); // deselect if already selected
            setselectedCombo({});
            setAlteredCombo({});
            return;
        }
        setSelectedTestCase(allTestcases.find(tc => tc.id === sel.id) as TestCase);
        const selCombo: TestComboSupportVal = allSupportedCombo.find(combo => combo.testCaseId === sel.id) ?? {};
        setselectedCombo(selCombo);
        setAlteredCombo(selCombo);
    }

    return <React.Fragment>
        <Typography variant='h4' paddingBottom={10}> 
            {selectedTestCombination.id && selectedInterface.id ? 'Configure Testcases for ' +
                selectedInterface.name + ' of ' + selectedTestCombination.combinationName
                : 'Testcases Settings. Please select a Test Combination & Interface to configure valid Testcases.'}
        </Typography>
        <Box sx={{ height: '80%', width: '100%' }}>
            <Grid container spacing={2} alignItems={'center'} >
                <Grid size={9} paddingBottom={10}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel shrink htmlFor='group'>{"Group"}</InputLabel>
                        <OutlinedInput notched name='group' id='group'
                            label={"Group"} value={selectedTestCase?.group}
                            onChange={(e) => setSelectedTestCase({ ...selectedTestCase, group: e.target.value })}
                        />
                    </FormControl>
                </Grid>
                <Grid size={1} />
                <Grid size={2} paddingBottom={10}>
                    <FormControlLabel control={<Checkbox
                        checked={alteredCombo.interfaceId !== undefined ? true : false}
                        inputProps={{ 'aria-label': 'controlled' }}
                        onChange={(e) => setAlteredCombo(e.target.checked ? { testCaseId: selectedTestCase.id, interfaceId: selectedInterface.id, testCombinationId: selectedTestCombination.id } : {})} />}
                        label="Valid"
                    />
                </Grid>
                <Grid size={12} paddingBottom={10}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel shrink htmlFor='name'>{"Name"}</InputLabel>
                        <OutlinedInput notched name='name' id='name'
                            label={"Name"} value={selectedTestCase?.name}
                            onChange={(e) => setSelectedTestCase({ ...selectedTestCase, name: e.target.value })}
                        />
                    </FormControl>
                </Grid>
                <Grid size={12} paddingBottom={10}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel shrink htmlFor='description'>{"Description"}</InputLabel>
                        <OutlinedInput notched name='description' id='description'
                            label={"Description"} value={selectedTestCase?.description ?? ''}
                            onChange={(e) => setSelectedTestCase({ ...selectedTestCase, description: e.target.value })}
                        />
                    </FormControl>
                </Grid>
                <Grid size={3} paddingBottom={10}>
                    <FormControl required fullWidth>
                        <Autocomplete id='tcft' options={TestcaseFieldTypeStrings}
                            value={TestcaseFieldTypeStrings[(selectedTestCase?.fieldType) ?? 2]}
                            onChange={(e, value) => setSelectedTestCase({ ...selectedTestCase, fieldType: TestcaseFieldTypeStrings.indexOf(value as string) })}
                            renderInput={(params) =>
                                <TextField name='tcft' multiline
                                    {...params}
                                    label={"Field Type"}
                                    sx={{
                                        '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                                            borderColor: 'divider',
                                        },
                                    }}
                                />
                            }
                        />
                    </FormControl>
                </Grid>
                <Grid size={9} paddingBottom={10}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel shrink htmlFor='fieldSchema'>{"Field Schema"}</InputLabel>
                        <OutlinedInput notched name='fieldSchema' id='fieldSchema'
                            label={"Field Schema"} value={selectedTestCase?.fieldSchema}
                            onChange={(e) => setSelectedTestCase({ ...selectedTestCase, fieldSchema: e.target.value })}
                        />
                    </FormControl>
                </Grid>
                <Grid size={3}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel shrink htmlFor='sortOrder'>{"Sort Order"}</InputLabel>
                        <OutlinedInput notched name='sortOrder' id='sortOrder'
                            label={"Sort Order"} value={selectedTestCase?.sortOrder}
                            onChange={(e) => setSelectedTestCase({ ...selectedTestCase, sortOrder: Number(e.target.value) })}
                        />
                    </FormControl>
                </Grid>
                <Grid size={3}>
                    <FormControl required fullWidth>
                        <Autocomplete id='tcStatus' options={TestcaseStatusStrings}
                            value={TestcaseStatusStrings[(selectedTestCase?.status) ?? 0]}
                            onChange={(e, value) => setSelectedTestCase({ ...selectedTestCase, status: TestcaseStatusStrings.indexOf(value as string) })}
                            renderInput={(params) =>
                                <TextField name='tcStatus' multiline
                                    {...params}
                                    label={"Status"}
                                    sx={{
                                        '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                                            borderColor: 'divider',
                                        },
                                    }}
                                />
                            }
                        />
                    </FormControl>
                </Grid>
                <Grid size={1} />
                <Grid size={2}>
                    <Button fullWidth startIcon={<ClearIcon />}
                        onClick={() => {
                            setSelectedTestCase(GetEmptyTestCase());
                        }}>
                        New
                    </Button>
                </Grid>
                <Grid size={1} />
                <Grid size={2} marginRight='0px' marginLeft='auto'>
                    <Button fullWidth startIcon={<SaveIcon />}
                        onClick={(e) => {
                            onSubmitHandler(e);
                        }}>
                        Save
                    </Button>
                </Grid>
            </Grid>
            <Box sx={{ width: '100%' }} marginTop={15}>
                <DataGrid
                    columns={TestcaseColumns}
                    rows={rowvalues}
                    columnVisibilityModel={{ id: false }}
                    onRowClick={onSelectionChange}
                    getRowClassName={(params) => params.row.id === selectedTestCase.id ? 'selectRowClass' : ''}
                    hideFooter
                    sx={{
                        "& .selectRowClass": {
                            color: 'primary.main'
                        },
                    }}
                />
            </Box>
        </Box>
    </React.Fragment>
}


